import apiService from './api';

export interface PositionAnalysis {
  _id: string;
  symbol: string;
  positionType: string;
  tradeLength: string;
  investmentObjective: string;
  currentPrice: number;
  aiAnalysis: string;
  analysisTimestamp: string;
  status: string;
  errorMessage?: string;
  processingTimeMs?: number;
  createdAt: string;
  updatedAt: string;
}

export interface AnalysisSummary {
  totalAnalyses: number;
  successfulAnalyses: number;
  failedAnalyses: number;
  lastAnalysisDate?: string;
  averageProcessingTimeMs: number;
}

export interface BulkAnalysisRequest {
  positionType: string;
  tradeLength: string;
  investmentObjective: string;
  riskTier: number;
  maxTickers?: number;
}

export interface BulkAnalysisResponse {
  success: boolean;
  totalTickers: number;
  analyzedTickers: number;
  failedTickers: number;
  processingTimeMs: number;
  results: Array<{
    symbol: string;
    success: boolean;
    error?: string;
    processingTimeMs?: number;
  }>;
}

export interface CleanupResponse {
  deletedCount: number;
}

class PositionAnalysisService {
  /**
   * Run bulk position analysis
   */
  async runBulkAnalysis(
    request: BulkAnalysisRequest,
  ): Promise<BulkAnalysisResponse> {
    const response = await apiService['api'].post(
      '/decision-engine/bulk-analysis/run',
      request,
    );
    return response.data;
  }

  /**
   * Get recent position analyses
   */
  async getRecentAnalyses(
    limit: number = 50,
    symbol?: string,
  ): Promise<PositionAnalysis[]> {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    if (symbol) params.append('symbol', symbol);

    const response = await apiService['api'].get(
      `/decision-engine/bulk-analysis/recent?${params.toString()}`,
    );
    return response.data;
  }

  /**
   * Get analysis summary
   */
  async getAnalysisSummary(): Promise<AnalysisSummary> {
    const response = await apiService['api'].get(
      '/decision-engine/bulk-analysis/summary',
    );
    return response.data;
  }

  /**
   * Clean up old analyses
   */
  async cleanupOldAnalyses(daysToKeep: number = 30): Promise<CleanupResponse> {
    const response = await apiService['api'].post(
      `/decision-engine/bulk-analysis/cleanup?daysToKeep=${daysToKeep}`,
    );
    return response.data;
  }
}

export const positionAnalysisService = new PositionAnalysisService();
